export const USECASE_ASSET_BASE_URL =
  "https://cdn.inito.com/inito_website/usecase/assets/assets";

export function usecaseAsset(path: string) {
  return `${USECASE_ASSET_BASE_URL}/${path}`;
}

/**
 * Per–use-case hero backgrounds (inito.com January 2026 / Figma).
 * Upload exports to the usecase CDN `assets/` folder using these filenames.
 */
export const usecaseHeroBackgrounds = {
  pcos: {
    mobile: usecaseAsset("hero_pcos_mobile.png"),
    desktop: usecaseAsset("hero_pcos_desktop.png"),
  },
  "trying-after-35": {
    mobile: usecaseAsset("hero_ttc35_mobile.png"),
    desktop: usecaseAsset("hero_ttc35_desktop.png"),
  },
  "after-miscarriage": {
    mobile: usecaseAsset("hero_miscarriage_mobile.png"),
    desktop: usecaseAsset("hero_miscarriage_desktop.png"),
  },
  "irregular-cycle": {
    mobile: usecaseAsset("hero_irc_mobile.png"),
    desktop: usecaseAsset("hero_irc_desktop.png"),
  },
  "before-ivf": {
    mobile: usecaseAsset("hero_ivf_mobile.png"),
    desktop: usecaseAsset("hero_ivf_desktop.png"),
  },
} as const;

export const usecaseAssets = {
  hero: {
    /** Legacy fallbacks; prefer usecaseHeroBackgrounds per route */
    mobile: usecaseAsset("hero-image-mobile.png"),
    desktop: usecaseAsset("usecase_desktop.png"),
  },
  whyInito: {
    desktop: usecaseAsset("why-inito.png"),
    mobile: usecaseAsset("why-inito-mobile.png"),
  },
  howInitoHelps: {
    confirmOvulation: usecaseAsset("confirm-ovulation.png"),
    decodeLhSurge: usecaseAsset("decode-lh-surge.png"),
    trackBetweenAppointments: usecaseAsset("track-between-appointments.png"),
    pcosValue1: usecaseAsset("usecase_pcos_value_1.png"),
    pcosValue2Frames: [
      usecaseAsset("usecase_pcos_value_2_1.png"),
      usecaseAsset("usecase_pcos_value_2_2.png"),
      usecaseAsset("usecase_pcos_value_2_3.png"),
    ],
    pcosValue3: usecaseAsset("usecase_pcos_value_3.png"),
    /** Trying after 35 — section 1 / shared with miscarriage & pre-IVF section 1 */
    ttc35Value1Frames: [
      usecaseAsset("usecase_ttc35_1_01.png"),
      usecaseAsset("usecase_ttc35_1_02.png"),
      usecaseAsset("usecase_ttc35_1_03.png"),
    ],
    ttc35Value2Frames: [
      usecaseAsset("usecase_ttc35_2_1.png"),
      usecaseAsset("usecase_ttc35_2_2.png"),
    ],
    ttc35Value3: usecaseAsset("usecase_ttc35_3.png"),
    /** Irregular cycle */
    ircValue1Frames: [
      usecaseAsset("usecase_irc_1_1.png"),
      usecaseAsset("usecase_irc_1_2.png"),
      usecaseAsset("usecase_irc_1_3.png"),
    ],
    ircValue2: usecaseAsset("usecase_irc_2.png"),
    ircValue3: usecaseAsset("usecase_irc_3.png"),
    /** After miscarriage (sections 2–3; section 1 uses ttc35Value1Frames) */
    postMiscarriageValue2: usecaseAsset("usecase_post_miscarriage_2.png"),
    postMiscarriageValue3: usecaseAsset("usecase_post_miscarriage_3.png"),
    /** Before IVF (sections 2–3; section 1 uses ttc35Value1Frames) */
    preIvfValue2Frames: [
      usecaseAsset("usecase_pre_ivf_2_1.png"),
      usecaseAsset("usecase_pre_ivf_2_2.png"),
      usecaseAsset("usecase_pre_ivf_2_3.png"),
    ],
    preIvfValue3: usecaseAsset("usecase_pre_ivf_3.png"),
    ttc35Value2Exact: usecaseAsset(
      "value-props/trying-after-35/value-2-exact.png",
    ),
  },
  doctors: {
    roxanne: usecaseAsset("dr-roxanne.png"),
    faith: usecaseAsset("dr-faith.png"),
    /** How Inito helps — expert quote section */
    expertPcos: usecaseAsset("expert-quote/pcos/dr-pero-desktop.png"),
    expertPcosMobile: usecaseAsset("expert-quote/pcos/dr-pero-mobile-exact.png"),
    expertTtc35:
      usecaseAsset("expert-quote/trying-after-35/doctor-desktop.png"),
    expertTtc35Mobile:
      usecaseAsset("expert-quote/trying-after-35/doctor-mobile.png"),
    expertTtc35MobileExact: usecaseAsset(
      "expert-quote/trying-after-35/doctor-mobile-exact.png",
    ),
    expertIrc:
      usecaseAsset("expert-quote/irregular-cycle/doctor-desktop.png"),
    expertIrcMobile: usecaseAsset("expert-quote/irregular-cycle/doctor-mobile.png"),
    expertPreIvf:
      usecaseAsset("expert-quote/before-ivf/doctor_desktop_2.png"),
    expertPreIvfMobile: usecaseAsset("usecase_pre_ivf_testimonial_new_2.png"),
    expertPostMiscarriage:
      usecaseAsset("expert-quote/after-miscarriage/doctor-desktop.png"),
    expertPostMiscarriageMobile: usecaseAsset(
      "expert-quote/after-miscarriage/doctor-mobile.png",
    ),
  },
  testimonials: {
    pcos1: usecaseAsset("usecase_pcos_1.png"),
    pcos2: usecaseAsset("usecase_pcos_2.jpeg"),
    pcos3: usecaseAsset("usecase_pcos_3.jpeg"),
    pcos4: usecaseAsset("usecase_pcos_4.jpeg"),
    pcosFeatured: usecaseAsset("usecase_pcos_testimonial_new.png"),
    ttc351: usecaseAsset("usecase_ttc35_1.jpg"),
    ttc352: usecaseAsset("usecase_ttc35_2.jpg"),
    ttc353: usecaseAsset("usecase_ttc_35_3.jpeg"),
    ttc354: usecaseAsset("usecase_ttc35_4.jpeg"),
    ttc35Featured: usecaseAsset("usecase_ttc35_testimonial_new.png"),
    irregularCycles1: usecaseAsset("usecase_irregular_cycles_1.jpg"),
    irregularCycles2: usecaseAsset("usecase_irregular_cycles_2.jpeg"),
    irregularCycles3: usecaseAsset("usecase_irregular_cycles_3.jpg"),
    irregularCycles4: usecaseAsset("usecase_irregular_cycles_4.jpeg"),
    irregularCyclesFeatured: usecaseAsset(
      "usecase_irregular_cycles_testimonial_new.png",
    ),
    postMiscarriage1: usecaseAsset("post_miscarriage_1.jpg"),
    postMiscarriage2: usecaseAsset("post_miscarriage_2.jpg"),
    postMiscarriage3: usecaseAsset("post_miscarriage_3.jpeg"),
    postMiscarriageFeatured: usecaseAsset("post_miscarriage_testimonial_new.png"),
    preIvf2: usecaseAsset("usecase_pre_ivf_2.jpeg"),
    preIvf3: usecaseAsset("usecase_pre_ivf_3.jpeg"),
    preIvfFeatured: usecaseAsset("usecase_pre_ivf_testimonial_new.png"),
  },
} as const;
